import { AlertCircle, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { LoadingSpinner } from "../ui/loadingSpinner/loadingSpinner";
import { InputBindingPicker } from "./InputBindingPicker";
import {
  INPUT_BINDING_COPY,
  targetsFilledBy,
  type BindingSubject,
  type BindingTarget,
  type BoundDocumentId,
  type FeedsReadState,
} from "../../lib/analyticsFeeds";

/**
 * The row's subject and the write a tick on it performs, as one value.
 *
 * They travel together because neither is meaningful without the other: the
 * subject is what decides which parameters render ticked, and the write is what
 * a tick stores. Held as two independent values, a cell could read one source
 * and write another, and nothing would say so until an auditor's tick landed on
 * a binding for a file they were not looking at.
 *
 * Composed here rather than folded into `BindingSubject` itself, which is the
 * domain vocabulary the pure feed functions and the picker speak: they answer
 * *what does this subject fill*, and a write is no part of that question.
 */
type BindingSubjectWithFill = BindingSubject & {
  onFill: (target: BindingTarget) => void;
};

/**
 * The box every state of this cell draws, live or inert.
 *
 * Held once because the column is read down as much as across: a row that
 * cannot be edited still has to put a control of the same height and width in
 * the same place, or the greying reads as a different control rather than as
 * this one turned off, and the column reads as ragged holes rather than as a
 * column.
 */
const TRIGGER_BOX_CLASS =
  "inline-flex h-9 w-full items-center gap-2 rounded-md border border-stone-200 px-2 text-sm";

/**
 * One row's `WORKFLOW PARAMETERS` control: which of the test table's
 * `(column, param_key)` pairs this row's subject fills.
 *
 * One component for every kind of row, because every row asks the same question
 * of the same list — a file's row and the generated sample row differ in what
 * fills the parameter, not in what may be chosen. Two components here would be
 * two vocabularies, and the auditor reads both rows in one table.
 *
 * The closed cell reports exactly what the list it opens has ticked, and nothing
 * else: it names the parameters this row's subject fills, and it stays silent
 * about parameters some other row answers. A cell that reported the table's
 * answers would print the same string on every row and attribute one row's
 * binding to another's file.
 *
 * Presentational. Every write is a callback, which is what lets the same cell
 * sit on a tab shared by non-analytics sessions without that tab growing a
 * conditional write path.
 */
interface InputBindingCellProps {
  subject: BindingSubjectWithFill;
  /**
   * What the trigger names itself after — the row's own title, because one
   * column heading cannot say which row a trigger belongs to.
   */
  subjectName: string;
  targets: readonly BindingTarget[];
  /** The session's file names, so the list can say which file fills a pair. */
  documentNames: ReadonlyMap<BoundDocumentId, string>;
  /**
   * What the feeds read has produced. Carried rather than inferred from
   * `targets`: an empty list is what an in-flight read, a read never issued, a
   * failed read and a workflow with no file parameters all look like here, and
   * only the last of those may be reported as such.
   *
   * A list known to be behind the server is still the list this cell draws. The
   * cell asserts nothing — it reports what the read holds and offers the writes
   * over it — and closing the editor over a failed background read would take
   * away the surface the auditor fixes their configuration from.
   */
  feedsState: FeedsReadState;
  /**
   * The table's analytics columns whose workflow is deleted. They contribute
   * nothing to `targets`, so without them an all-deleted table would be drawn
   * as a workflow that declares no file inputs.
   */
  deletedColumnNames: readonly string[];
  pending: boolean;
  onClear: (target: BindingTarget) => void;
  /**
   * Whether this row is barred from filling anything at all — not busy, not
   * empty, but ineligible, which for a document row is its analytics-input flag
   * settled as No.
   *
   * The inert state lives here rather than at each caller so that both states
   * of the control are drawn by the one component that owns its shape. A caller
   * rendering its own greyed stand-in would be a second copy of this box to keep
   * in step with this one, and the two would drift in the one dimension that
   * matters — where they sit in a column the auditor reads down.
   *
   * Distinct from `pending`, which greys a control the auditor may still use in
   * a moment, and from an empty `targets`, which is a fact about the workflow.
   * Ineligibility is a fact about the row, and it is answered in another column.
   */
  disabled?: boolean;
  /** Holds the picker read-only and shows this reason above it, or null. */
  writesHeldReason?: string | null;
}

export function InputBindingCell({
  subject,
  subjectName,
  targets,
  documentNames,
  feedsState,
  deletedColumnNames,
  pending,
  onClear,
  disabled = false,
  writesHeldReason = null,
}: InputBindingCellProps) {
  if (disabled) {
    return (
      // No click is swallowed here, unlike the live cell: nothing in this box
      // acts, so a press inside it can only mean what a press anywhere else on
      // the row means.
      <div className="p-2">
        {/* Disabled natively rather than styled to look it. The attribute is
            what takes the control out of the tab order and has a screen reader
            announce it as unavailable, so the offer of an action matches what
            pressing would achieve. Its accessible name still carries the column
            and the row, because a control that says only "Fills nothing" leaves
            a non-sighted reader to guess what is filling nothing. */}
        <button
          type="button"
          disabled
          aria-label={`${INPUT_BINDING_COPY.parametersColumnHeading} — ${subjectName}`}
          className={`${TRIGGER_BOX_CLASS} cursor-not-allowed bg-stone-50 text-font-secondary`}
        >
          <span className="truncate">
            {INPUT_BINDING_COPY.cellFillsNothing}
          </span>
          <ChevronDown className="ml-auto h-4 w-4 shrink-0" />
        </button>
      </div>
    );
  }

  const filled = targetsFilledBy(targets, subject);
  // The cell names one parameter, and a row may fill several. The count of the
  // rest is carried beside it rather than dropped: a cell showing the first of
  // two names understates what this row is answering.
  const selected = filled.length === 0 ? null : filled[0];
  const otherSelectedCount = filled.length - 1;

  return (
    <div className="p-2" onClick={(event) => event.stopPropagation()}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            // Announced as the select it is, so the closed cell is not read as a
            // button that does something on press.
            role="combobox"
            aria-label={`${INPUT_BINDING_COPY.parametersColumnHeading} — ${subjectName}`}
            className={`group ${TRIGGER_BOX_CLASS} bg-white`}
          >
            {selected !== null ? (
              <>
                <span className="truncate">{selected.label}</span>
                {otherSelectedCount > 0 && (
                  <span className="shrink-0 text-xs text-font-secondary">
                    +{otherSelectedCount}
                  </span>
                )}
              </>
            ) : (
              <span className="truncate text-font-secondary">
                {INPUT_BINDING_COPY.cellNoAssignments}
              </span>
            )}
            {/* One glyph rotated rather than two swapped, which is how the
                Sample Match and Document Category cells on this same row draw
                theirs. Read off the trigger's own `aria-expanded`, so the
                direction cannot disagree with the popover it points at — a
                second copy of the open state is a second thing to keep in
                step. */}
            <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-font-secondary transition-transform group-aria-expanded:rotate-180" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-96" align="end">
          {writesHeldReason !== null && (
            <p className="mb-2 text-xs text-font-secondary">
              {writesHeldReason}
            </p>
          )}
          {feedsState === "loading" ? (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner />
            </div>
          ) : /* `unasked` draws the unavailable line and not the spinner: the
                spinner promises an answer that is on its way, and no read is
                running to bring one, so it would turn instead of resolving for
                as long as the popover stayed open. Nor may it fall through to
                the empty-feeds line below, which states a fact about the
                workflow — that it declares no file inputs — that only an answer
                can establish. What is true without one is that nothing can be
                assigned here. */
          feedsState === "failed" || feedsState === "unasked" ? (
            <div className="flex items-start gap-2 text-xs text-red-600">
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{INPUT_BINDING_COPY.feedsUnavailable}</span>
            </div>
          ) : targets.length === 0 ? (
            <div className="text-xs text-font-secondary">
              {deletedColumnNames.length > 0
                ? INPUT_BINDING_COPY.workflowDeleted(deletedColumnNames)
                : INPUT_BINDING_COPY.emptyFeeds}
            </div>
          ) : (
            <>
              <InputBindingPicker
                targets={targets}
                subject={subject}
                documentNames={documentNames}
                pending={pending || writesHeldReason !== null}
                onFill={subject.onFill}
                onClear={onClear}
              />
              {/* Beside the live list rather than instead of it: the live
                  columns can still be fed, and a picker that silently lacked a
                  column would read as that column taking no files. */}
              {deletedColumnNames.length > 0 && (
                <div className="mt-2 text-xs text-font-secondary">
                  {INPUT_BINDING_COPY.workflowDeleted(deletedColumnNames)}
                </div>
              )}
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
